import { app } from "js/namespaces";
import React, { Component } from "react";
import styled from "styled-components";
import { FlexBox, ScrollBox, AbsoluteBox } from "../../../react/components/LayoutGrid";
import { themeColors } from "../../../react/sharedStyles";
import PresentationEditorController, { SLIDE_NAVIGATOR_TRANSITION_DURATION } from "../../PresentationEditor/PresentationEditorController";
import { ClickAwayListener, Popover } from "@mui/material";
import { _ } from "js/vendor";
import { Transition } from "react-transition-group";
import { Button } from "../../../Components/Button";
import { Icon } from "../../../Components/Icon";
import { IconButton } from "../../../Components/IconButton";
import { Slider } from "../../../Components/Slider";
import { ToolTip } from "../../../Components/ToolTip";
import { ds } from "../../../core/models/dataService";
import { Key } from "js/core/utilities/keys";
import * as geom from "../../../core/utilities/geom";
import Avatar from "../../../react/components/Avatar";
import { FlexSpacer } from "../../../react/components/Gap";
import { SlideActionsPopupMenu } from "./SlideActionsPopupMenu";
import { SlideActionsController } from "./SlideActionsController";
import SlideThumbnail from "./SlideThumbnail";

export const SLIDE_NAVIGATOR_WIDTH = 280;

const TRANSITION_DURATION = SLIDE_NAVIGATOR_TRANSITION_DURATION;

const SlideGrid = styled(ScrollBox)`
    position: relative;
    width: 100%;
    flex-grow: 2;
    position: relative;
    min-height: 400px;

    ::-webkit-scrollbar-thumb:vertical {
        border-radius: 20px;
        background-color: #4b4e55;
        border: 8px solid transparent;
        background-clip: content-box;
    }

    &:focus {
        background-color: red;
    }
`;

const SelectionMarquee = styled.div.attrs(props => ({
    style: {
        left: props.left,
        top: props.top,
        width: props.width,
        height: props.height
    }
}))`
    position: absolute;
    border: 1px dashed ${themeColors.ui_blue};
    background: rgba(30, 144, 255, 0.12);
    pointer-events: none;
    z-index: 1000;
`;

const SlideControls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 20px;
    align-items: flex-end;
`;

const SlideNumber = styled.div`
    margin-right: 5px;
    font-size: 14px;
    font-weight: 600;
    text-align: right;
    border-left: ${props => props.selected ? `solid 2px ${themeColors.ui_blue}` : "solid 2px transparent"};
    padding-left: 5px;
`;

const SkippedSlideBadge = styled.div`
    background: #e8950b;
    border-radius: 50%;
    padding: 3px 3px;

    .bai-icon {
        font-size: 12px;
    }
`;

const LibrarySlideBadge = styled.div`
    background: orangered;
    border-radius: 50%;
    padding: 3px 3px;

    .bai-icon {
        font-size: 12px;
    }
`;

const Collaborator = styled.div`
    position: absolute;
    bottom: 9px;
    right: 8px;
    border: solid 1px white;
    border-radius: 50%;
    transition: transform ${TRANSITION_DURATION}ms, right ${TRANSITION_DURATION}ms;
    transform-origin: bottom right;
`;

const SlideThumbnailContainer = styled.div.attrs(props => ({
    style: {
        width: props.width,
        height: props.height,
        left: props.left,
        top: props.top,
        transition: props.isDragging ? "none" : null,
        zIndex: props.isDragging ? (props.layer ?? 0) : 1,
        cursor: props.isDragging ? "grabbing" : (props.isMouseDown ? "grab" : "pointer"),
        transform: `rotate(${props.isDragging ? (props.rotation ?? 0) : 0}deg)`,
    }
}))`
    position: absolute;
    display: flex;
    gap: 5px;

    ${SlideControls} {
        opacity: ${props => props.isDragging ? 0 : 1};
    }
    
    .slide-menu {
        opacity: 0;
        transition: opacity 150ms;
    }
    &:hover{
        .slide-menu {
            opacity: 1 !important;
        }
    }
`;

const Container = styled.div.attrs(props => ({
    style: {
        width: (props.isExpanded || props.isOutlineView) ? "100vw" : (props.width ?? SLIDE_NAVIGATOR_WIDTH),
        left: (props.open || props.isExpanded || props.isOutlineView) ? 0 : -(props.width ?? SLIDE_NAVIGATOR_WIDTH)
    }
}))`
    height: 100%;
    background: #2f2f2f;
    border-top: solid 1px #444;
    z-index: 5;
    pointer-events: all;
    position: absolute;
    left: 0px;
    transition: ${props => props.transition ? `left ${TRANSITION_DURATION}ms, width ${TRANSITION_DURATION}ms` : `left ${TRANSITION_DURATION}ms`};
    box-sizing: border-box;
    overflow: hidden;
    color: white;
    display: flex;

    .canvas-frame {
        transition: ${props => props.transition ? `width ${TRANSITION_DURATION}ms, height ${TRANSITION_DURATION}ms` : "none"};
    }

    .slide_canvas {
        transition: ${props => props.transition ? `transform ${TRANSITION_DURATION}ms` : "none"};
    }

    ${SlideThumbnailContainer} {
        transition: ${props => props.transition ? `left ${TRANSITION_DURATION}ms, top ${TRANSITION_DURATION}ms, width ${TRANSITION_DURATION}ms, height ${TRANSITION_DURATION}ms` : "none"};
    }

    &.light {
        background: white;
        border-top: none;
        color: #333;

        ${SlideGrid} {
            ::-webkit-scrollbar-thumb:vertical {
                background-color: #c8ced4 !important;
            }
        }

        .bai-button .bai-icon {
            color: #333 !important;
        }
    }
`;

const SlideGridContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
`;

const MultiDragBadge = styled.div`
    position: absolute;
    background: ${themeColors.ui_blue};
    color: white;
    border-radius: 50%;
    padding: 3px;
    font-size: 13px;
    font-weight: bold;
    width: 20px;
    height: 20px;
    top: -10px;
    right: -10px;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ResizeHandle = styled.div`
    right: 0;
    background: ${props => props.resizing ? "rgba(255,255,255,0.12)" : undefined};
    position: absolute;
    top: 0;
    right: 0;
    width: 6px;
    height: 100%;
    cursor: ew-resize;
    z-index: 200;
    background: transparent;
    transition: background 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
    }
`;

const AddSlideButton = styled.div`
    position: absolute;
    bottom: -42px;
    left: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
        font-size: 12px;
        text-wrap: nowrap;
    }

    .bai-icon {
        font-size: 22px !important;
        color: white !important;
        font-variation-settings: "FILL" 0, "wght" 300, "GRAD" 0, "opsz" 2 !important;
    }
`;

const ThumbnailScaleSlider = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    .bai-slider {
        position: relative;
        top: 1px;
    }

    .bai-icon {
        color: white !important;
    }
`;

const ControlBar = styled.div`
    display: flex;
    background: #2e3335;
    border-top: solid 1px #666;
    width: 100%;
    height: 50px;
    padding: 10px 10px 10px 10px;
    align-items: center;
    gap: 20px;

    &.small {
        padding: 10px 10px;

        ${AddSlideButton} {
            span {
                font-size: 12px;
            }

            .bai-icon {
                font-size: 26px !important;
            }
        }
    }

    label {
        color: white;
        font-size: 14px;
        font-weight: bold;
    }

    .bai-icon {
        color: white;
    }

    .bai-button {
        color: white;
        font-size: 14px;

        .bai-icon {
            font-size: 36px;
            font-variation-settings: "FILL" 1, "wght" 400, "GRAD" 0, "opsz" 2;
        }
    }

    &.light {
        background: white;

        label {
            color: #333;
        }

        .bai-icon {
            color: #515c66 !important;
        }

        .bai-button {
            color: #333;
        }

        .slider {
            span {
                color: #333;
            }

            ::webkit-slider-thumb {
                background: ${themeColors.ui_blue};
            }
        }
    }
`;

const LAYOUT_STYLE_PROPS = {
    paddingLeft: 30,
    paddingTop: 20,
    slideControlWidth: 20,
    vGap: 40
};

class SlideNavigatorPanel extends Component {
    get isNav() {
        return this.props.slideNavMode == "nav";
    }

    get isGrid() {
        return this.props.slideNavMode == "grid";
    }

    get isOutlineView() {
        return this.props.slideNavMode == "outline";
    }

    constructor(props) {
        super(props);

        this.state = {
            slideLayout: [],
            selectedSlides: [props.currentSlide],
            contextMenu: false,
            scale: 3,
            collaborators: [],
            isMouseDown: false,
            slideNavWidth: props.slideNavWidth,
            isSelecting: false,
            selectionBox: null,
        };

        this.slideGridRef = React.createRef();
        this.containerRef = React.createRef();
        this.slidesMetadata = {};
        this.dragPosition = null;
        this.mouseDownOffset = null;
        this.mouseDownPosition = null;
        this.selectionDrag = null;
        this.shouldScrollToSelection = true;
        this.isNavModeTransitioning = false;
        this.navModeTransitionTimeout = null;
    }

    componentDidMount() {
        this.collaboratorsSubscription = ds.getObservables().collaborators$
            .subscribe(collaborators => {
                this.setState({
                    collaborators: Object.values(collaborators)
                        .map(collaborator => ({
                            ...collaborator,
                            displayName: collaborator.displayName === collaborator.email ? undefined : collaborator.displayName,
                        })).filter(({ uid }) => uid !== app.user.id)
                });
            });

        PresentationEditorController.slidesMetadataLoadPromise.then(slidesMetadata => {
            this.slidesMetadata = slidesMetadata;
        });

        this.calcLayout(false);
        window.addEventListener("resize", this.handleResize);
        document.addEventListener("keydown", this.handleGridKeyDown, true);

        _.defer(() => {
            this.scrollToSlide(this.props.currentSlide, false);
        });
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.slideNavWidth != this.props.slideNavWidth) {
            this.setState({ slideNavWidth: this.props.slideNavWidth });
        }

        if (prevProps.slideNavMode != this.props.slideNavMode) {
            clearTimeout(this.navModeTransitionTimeout);
            this.isNavModeTransitioning = true;
            window.requestAnimationFrame(this.handleScrollFrame);
            this.navModeTransitionTimeout = setTimeout(() => {
                this.isNavModeTransitioning = false;
            }, TRANSITION_DURATION);
            this.calcLayout(true);
        } else if (prevState.slideNavWidth != this.state.slideNavWidth) {
            this.calcLayout(false);
            this.scrollToSlide(this.state.selectedSlides[0], false);
        } else if (prevProps.currentSlide != this.props.currentSlide) {
            if (!this.state.selectedSlides.includes(this.props.currentSlide)) {
                this.shouldScrollToSelection = true;
                this.setState({ selectedSlides: [this.props.currentSlide] });
            }
        } else if (prevProps.slides != this.props.slides) {
            this.calcLayout(false);
        } else if (prevState.mouseOverSlide != this.state.mouseOverSlide || prevState.scale != this.state.scale) {
            this.calcLayout(true);
        }

        if (!_.isEqual(prevState.selectedSlides.map(s => s.id), this.state.selectedSlides.map(s => s.id))) {
            this.calcLayout(true);
            if (this.shouldScrollToSelection && this.state.selectedSlides.length === 1) {
                this.scrollToSlide(this.state.selectedSlides[0], true);
            }
            this.shouldScrollToSelection = true;
        }
    }

    componentWillUnmount() {
        this.collaboratorsSubscription?.unsubscribe();
        window.removeEventListener("resize", this.handleResize);
        document.removeEventListener("keydown", this.handleGridKeyDown, true);
        window.removeEventListener("mousemove", this.handleSelectionMouseMove);
        window.removeEventListener("mouseup", this.handleSelectionMouseUp);
    }

    handleGridKeyDown = event => {
        if (!this.isGrid) return;

        const target = event.target;
        const tag = target?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
        const { selectedSlides } = this.state;

        switch (event.which) {
            case Key.DELETE:
            case Key.BACKSPACE:
                event.preventDefault();
                event.stopPropagation();
                SlideActionsController.deleteSlides(selectedSlides);
                return;
        }

        const isMeta = event.metaKey || event.ctrlKey;
        if (!isMeta) return;

        if (event.which === Key.KEY_D) {
            event.preventDefault();
            event.stopPropagation();
            SlideActionsController.duplicateSlides(selectedSlides);
        } else if (event.which === Key.KEY_C) {
            event.preventDefault();
            event.stopPropagation();
            if (selectedSlides?.length) {
                SlideActionsController.copySlides(selectedSlides);
            }
        } else if (event.which === Key.KEY_X) {
            event.preventDefault();
            event.stopPropagation();
            if (selectedSlides?.length) {
                SlideActionsController.cutSlides(selectedSlides);
            }
        } else if (event.which === Key.KEY_V) {
            event.preventDefault();
            event.stopPropagation();
            SlideActionsController.pasteSlides(selectedSlides || [], event);
        }
    }

    handleCloseGrid = () => {
        PresentationEditorController.setSlideNavMode("nav");
        return new Promise(resolve => {
            _.delay(() => {
                resolve();
            }, TRANSITION_DURATION);
        });
    }

    handleResize = () => {
        this.calcLayout(false);
    }

    handleScroll = () => {
        const { isDragging, isSelecting } = this.state;
        if (!isDragging && !isSelecting) {
            return;
        }

        this.calcLayout(true);
    }

    calcGridLayout() {
        let { slides } = this.props;
        let { isDragging, selectedSlides, scale } = this.state;

        let { paddingTop, slideControlWidth } = LAYOUT_STYLE_PROPS;

        const paddingLeft = 30;

        let x = paddingLeft;
        let y = paddingTop;

        let colCount = 8 - scale; // 3 to 8 columns
        let vGap = 40;
        let hGap = 30 + 5 * scale; // reduce gap as scale increases
        let width = (window.innerWidth - paddingLeft * 2 - hGap * (colCount - 1)) / colCount;

        let height = (width - slideControlWidth) * 9 / 16;

        let addSpace = () => {
            x += width + hGap;
            if (x + width > window.innerWidth) {
                x = paddingLeft;
                y += height + vGap;
            }
        };

        let layout = [];
        let index = 0;
        let dropIndex = null;
        let dragPosition = isDragging ? new geom.Point(this.dragPosition.x, this.dragPosition.y + this.slideGridRef.current.ref.current.scrollTop) : null;
        let dragBounds = isDragging ? new geom.Rect(dragPosition.x, dragPosition.y, width, height) : null;
        for (let slide of slides) {
            if (isDragging && selectedSlides.contains(slide)) {
                // Dragging slides - floating
                layout.push({ x: dragPosition.x, y: dragPosition.y, width, height, slide });
                continue;
            }

            let bounds = new geom.Rect(x, y, width, height);

            if (
                isDragging &&
                dropIndex === null &&
                bounds.intersects(dragBounds)
            ) {
                // Render empty drop zone
                addSpace();
                dropIndex = index;
            }

            // Render actual slide
            layout.push({ x, y, width, height, slide });
            addSpace();
            index++;
        }

        if (isDragging && dropIndex === null) {
            dropIndex = index;
        }

        y += height + vGap;

        return { layout, dropIndex, totalHeight: y };
    }

    calcNavLayout() {
        let { slides } = this.props;
        let { isDragging, selectedSlides, slideNavWidth } = this.state;

        let { paddingTop, slideControlWidth } = LAYOUT_STYLE_PROPS;

        const paddingLeft = 10;
        let vGap = 20;

        let x = paddingLeft;
        let y = paddingTop;

        let width = slideNavWidth - paddingLeft * 2 - 20;
        let height = (width - slideControlWidth) * 9 / 16;

        let addSpace = () => {
            y += height + vGap;
        };

        let layout = [];
        let index = 0;
        let dropIndex = null;
        let dragPosition = isDragging ? new geom.Point(this.dragPosition.x, this.dragPosition.y + this.slideGridRef.current.ref.current.scrollTop) : null;

        if (dragPosition && dragPosition.y < y) {
            addSpace();
            dropIndex = 0;
        }

        for (let slide of slides) {
            if (isDragging && selectedSlides.contains(slide)) {
                // Dragging slides - floating
                layout.push({ x: dragPosition.x, y: dragPosition.y, width, height, slide });
                continue;
            }

            if (
                isDragging &&
                dropIndex === null &&
                dragPosition.y >= y &&
                dragPosition.y <= y + height + vGap
            ) {
                // Render empty drop zone
                addSpace();
                dropIndex = index;
            }

            // Render actual slide
            layout.push({ x, y, width, height, slide });
            addSpace();
            index++;

            if (selectedSlides.length == 1 && slide == selectedSlides[0]) {
                y += 30;
            }
        }

        if (dragPosition && dragPosition.y > y) {
            dropIndex = index;
        }

        return { layout, dropIndex, totalHeight: y };
    }

    calcLayout(transition = true) {
        let { isDragging } = this.state;

        let { layout, dropIndex, totalHeight } = this.isGrid ? this.calcGridLayout() : this.calcNavLayout();

        this.setState(state => ({
            ...state,
            slideLayout: layout,
            transition,
            dropIndex: isDragging ? dropIndex : state.dropIndex,
            totalHeight
        }));
    }

    scrollToSlide(slide, animate = true) {
        let { layout } = this.isGrid ? this.calcGridLayout() : this.calcNavLayout();

        let slideLayout = layout.find(layout => layout.slide == slide);
        if (slideLayout) {
            this.slideGridRef.current.ref.current.scrollTo({
                top: slideLayout.y + slideLayout.height / 2 - (window.innerHeight / 2 - 105),
                behavior: animate ? "smooth" : "instant",
            });
        }
    }

    handleMouseDown = (event, slide) => {
        if (event.target?.closest && event.target.closest(".slide-menu")) {
            return;
        }
        let { slides } = this.props;
        let { selectedSlides, isDragging, slideLayout } = this.state;

        event.stopPropagation();

        this.setState({ isMouseDown: true });

        if (isDragging) {
            return;
        }

        if (event.button == 2) {
            // show popup menu
            if (!selectedSlides.includes(slide)) {
                PresentationEditorController.setCurrentSlide(slide);
            }
            this.setState({ contextMenu: { slide, x: event.clientX, y: event.clientY } });
            return;
        }

        let selection;
        const isMultiModifier = event.metaKey || event.ctrlKey || event.shiftKey;
        this.shouldScrollToSelection = !isMultiModifier;

        if (event.metaKey || event.ctrlKey) {
            // add/remove from selection
            if (selectedSlides.includes(slide) && selectedSlides.length > 1) {
                selection = selectedSlides.filter(s => s != slide);
            } else {
                selection = [...selectedSlides, slide];
            }
        } else if (event.shiftKey) {
            // range select
            let firstIndex = _.min(selectedSlides.map(s => s.getIndex()));
            let lastIndex = _.max(selectedSlides.map(s => s.getIndex()));
            if (slide.getIndex() < firstIndex) {
                selection = [...selectedSlides, slides.filter(s => s.getIndex() >= slide.getIndex() && s.getIndex() <= firstIndex)];
            } else {
                selection = [...selectedSlides, slides.filter(s => s.getIndex() >= lastIndex && s.getIndex() <= slide.getIndex())];
            }
            selection = _.uniq(selection.flat());
        } else {
            // single select
            if (selectedSlides.length > 1) {
                selection = [slide];
            } else if (!selectedSlides.includes(slide)) {
                selection = [slide];
            } else {
                selection = selectedSlides;
            }
        }

        this.setState({ selectedSlides: selection, targetSlide: slide });

        // prepare for drag
        this.mouseDownPosition = new geom.Point(event.clientX, event.clientY);
        const scrollTop = this.slideGridRef.current.ref.current.scrollTop;
        let { x, y } = slideLayout.find(layout => layout.slide == slide);
        this.mouseDownOffset = this.mouseDownPosition.minus(x, y - scrollTop);
        window.addEventListener("mousemove", this.handleMouseMove);
        window.addEventListener("mouseup", this.handleMouseUp);
    }

    getGridPoint(event) {
        const grid = this.slideGridRef.current?.ref?.current;
        if (!grid) {
            return new geom.Point(event.clientX, event.clientY);
        }
        const rect = grid.getBoundingClientRect();
        return new geom.Point(event.clientX - rect.left, event.clientY - rect.top + grid.scrollTop);
    }

    handleGridMouseDown = event => {
        if (!this.isGrid) return;
        if (event.button !== 0) return;
        if (this.state.selectedSlides.length > 0) {
            this.setState({ selectedSlides: [] });
        }
        if (event.target?.closest && event.target.closest(".slide-thumbnail")) return;

        event.preventDefault();
        event.stopPropagation();

        const startPoint = this.getGridPoint(event);
        this.selectionDrag = { startPoint };
        this.shouldScrollToSelection = false;
        this.setState({
            isSelecting: true,
            selectionBox: {
                left: startPoint.x,
                top: startPoint.y,
                width: 0,
                height: 0
            }
        });

        window.addEventListener("mousemove", this.handleSelectionMouseMove);
        window.addEventListener("mouseup", this.handleSelectionMouseUp);
    }

    handleSelectionMouseMove = event => {
        if (!this.selectionDrag) return;
        const grid = this.slideGridRef.current?.ref?.current;
        if (grid) {
            const rect = grid.getBoundingClientRect();
            const edgeThreshold = 40;
            let scrollDelta = 0;
            if (event.clientY < rect.top + edgeThreshold) {
                scrollDelta = -Math.min(edgeThreshold, rect.top + edgeThreshold - event.clientY);
            } else if (event.clientY > rect.bottom - edgeThreshold) {
                scrollDelta = Math.min(edgeThreshold, event.clientY - (rect.bottom - edgeThreshold));
            }
            if (scrollDelta !== 0) {
                grid.scrollBy({ top: scrollDelta, behavior: "instant" });
            }
        }

        const currentPoint = this.getGridPoint(event);
        const { startPoint } = this.selectionDrag;
        const left = Math.min(startPoint.x, currentPoint.x);
        const top = Math.min(startPoint.y, currentPoint.y);
        const width = Math.abs(startPoint.x - currentPoint.x);
        const height = Math.abs(startPoint.y - currentPoint.y);

        this.setState({
            selectionBox: { left, top, width, height }
        });
    }

    handleSelectionMouseUp = event => {
        const { selectionBox, selectedSlides, slideLayout } = this.state;
        const isMultiModifier = event.metaKey || event.ctrlKey || event.shiftKey;
        this.selectionDrag = null;

        window.removeEventListener("mousemove", this.handleSelectionMouseMove);
        window.removeEventListener("mouseup", this.handleSelectionMouseUp);

        if (!selectionBox || selectionBox.width < 4 || selectionBox.height < 4) {
            this.shouldScrollToSelection = true;
            this.setState({ isSelecting: false, selectionBox: null });
            return;
        }

        const selectionRect = new geom.Rect(selectionBox.left, selectionBox.top, selectionBox.width, selectionBox.height);
        const hits = slideLayout
            .filter(layout => selectionRect.intersects(new geom.Rect(layout.x, layout.y, layout.width, layout.height)))
            .map(layout => layout.slide);

        if (hits.length) {
            const nextSelection = isMultiModifier
                ? _.uniq([...selectedSlides, ...hits])
                : hits;
            const selectionChanged = !_.isEqual(
                nextSelection.map(slide => slide.id),
                selectedSlides.map(slide => slide.id)
            );
            if (!selectionChanged) {
                this.shouldScrollToSelection = true;
            }
            this.setState({ selectedSlides: nextSelection });
        } else {
            this.shouldScrollToSelection = true;
        }

        this.setState({ isSelecting: false, selectionBox: null });
    }

    handleMouseMove = event => {
        let { isDragging } = this.state;

        event.stopPropagation();

        let mousePosition = new geom.Point(event.clientX, event.clientY);
        if (!isDragging) {
            if (this.mouseDownPosition.distance(mousePosition) > 10) {
                this.setState({ isDragging: true });
                window.requestAnimationFrame(this.handleScrollFrame);
            } else {
                return;
            }
        }

        this.dragPosition = new geom.Point(mousePosition.x - this.mouseDownOffset.x, mousePosition.y - this.mouseDownOffset.y);
        this.calcLayout();
    }

    handleScrollFrame = timestamp => {
        const { isDragging, totalHeight, slideLayout, selectedSlides } = this.state;

        if (isDragging) {
            if (!this.lastScrollFrameTimestamp) {
                this.lastScrollFrameTimestamp = timestamp;
                window.requestAnimationFrame(this.handleScrollFrame);
                return;
            }

            let frameSize = timestamp - this.lastScrollFrameTimestamp;
            let viewHeight = window.innerHeight - 105;
            let min = viewHeight * 0.2;
            let max = viewHeight * 0.8;
            if (this.dragPosition.y < min || this.dragPosition.y > max) {
                let currentScrollTop = this.slideGridRef.current.ref.current.scrollTop;
                if (currentScrollTop + viewHeight < totalHeight + _.last(slideLayout).height) {
                    let velocity = (this.dragPosition.y < min ? this.dragPosition.y - min : this.dragPosition.y - max) / 70;
                    this.slideGridRef.current.ref.current.scrollBy({
                        top: velocity * frameSize,
                        behavior: "instant",
                    });
                }
            }

            this.lastScrollFrameTimestamp = timestamp;
            window.requestAnimationFrame(this.handleScrollFrame);
            return;
        }

        if (this.isNavModeTransitioning && selectedSlides[0]) {
            this.scrollToSlide(selectedSlides[0], false);
            window.requestAnimationFrame(this.handleScrollFrame);
            return;
        }

        this.lastScrollFrameTimestamp = null;
    }

    handleMouseUp = event => {
        const { selectedSlides, isDragging, dropIndex, targetSlide } = this.state;

        clearInterval(this.scrollInterval);

        window.removeEventListener("mousemove", this.handleMouseMove);
        window.removeEventListener("mouseup", this.handleMouseUp);

        if (isDragging) {
            PresentationEditorController.presentation.moveSlides(selectedSlides.map(slide => slide.id), dropIndex);
        }

        this.setState({ isDragging: false, isMouseDown: false });
        if (this.isNav && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
            PresentationEditorController.setCurrentSlide(targetSlide);
        }
    }

    handleDoubleClick = slide => {
        if (!this.isNav) {
            this.setState({ selectedSlides: [slide] });
            this.handleCloseGrid();
            PresentationEditorController.setCurrentSlide(slide);
        }
    }

    handleResizeMouseDown = event => {
        window.addEventListener("mousemove", this.handleResizeMouseMove);
        window.addEventListener("mouseup", this.handleResizeMouseUp);
    }

    handleResizeMouseMove = event => {
        this.setState({ slideNavWidth: Math.clamp(event.clientX, 200, 500) });
    }

    handleResizeMouseUp = event => {
        PresentationEditorController.setSlideNavWidth(this.state.slideNavWidth);

        window.removeEventListener("mousemove", this.handleResizeMouseMove);
        window.removeEventListener("mouseup", this.handleResizeMouseUp);
    }

    handleSetScale = scale => {
        this.setState({ scale: Math.clamp(scale, 0, 5) });
    }

    render() {
        const { slides, presentation, uiTheme, showSlideNav, slideNavWidth, currentTheme } = this.props;
        const { slideLayout, selectedSlides, contextMenu, isDragging, scale, resizing, collaborators, transition, totalHeight, isSelecting, selectionBox } = this.state;

        if (slideLayout.length == 0) return null;

        let buttonColor = uiTheme == "dark" ? "#eee" : "#333";
        let hoverColor = uiTheme == "dark" ? "#444" : "#eee";

        const MAX_USERS_TO_RENDER = 10;

        const selectedSlide = selectedSlides[0];

        return (
            <Transition in={this.isGrid} timeout={TRANSITION_DURATION} nodeRef={this.containerRef}>
                {transitionState => (
                    <Container
                        ref={this.containerRef}
                        id="slide-navigator"
                        open={showSlideNav}
                        width={slideNavWidth}
                        isExpanded={this.isGrid}
                        isOutlineView={this.isOutlineView}
                        transition={transition}
                        className={uiTheme}
                        onMouseDown={event => event.stopPropagation()}
                    >
                        {!this.isGrid && (
                            <ResizeHandle
                                resizing={resizing}
                                onMouseDown={this.handleResizeMouseDown}
                            />
                        )}
                        <SlideGridContainer>
                            <SlideGrid id="slide-navigator-grid" ref={this.slideGridRef} onScroll={this.handleScroll} onMouseDown={this.handleGridMouseDown}>
                                {slideLayout.map((slideLayout, i) => (
                                    <SlideThumbnailContainer key={i} className="slide-thumbnail"
                                        left={slideLayout.x}
                                        top={slideLayout.y}
                                        width={this.isOutlineView ? "100%" : slideLayout.width}
                                        height={slideLayout.height}
                                        isDragging={isDragging && selectedSlides.includes(slideLayout.slide)}
                                        isMouseDown={this.state.isMouseDown}
                                        onMouseDown={e => this.handleMouseDown(e, slideLayout.slide)}
                                        onDoubleClick={() => this.handleDoubleClick(slideLayout.slide)}
                                        rotation={selectedSlides.indexOf(slideLayout.slide) * 20 / selectedSlides.length}
                                        layer={100 - selectedSlides.indexOf(slideLayout.slide)}
                                    >
                                        <SlideControls>
                                            <SlideNumber selected={selectedSlide === slideLayout.slide}>{i + 1}</SlideNumber>
                                            {presentation.isSlideSkipped(slideLayout.slide, this.slidesMetadata) && <SkippedSlideBadge><Icon white fill>visibility_off</Icon></SkippedSlideBadge>}
                                            {slideLayout.slide.isLibrarySlide() && <LibrarySlideBadge><Icon white fill>lock</Icon></LibrarySlideBadge>}
                                            <FlexSpacer />
                                            <IconButton className="slide-menu" icon="more_vert" color={buttonColor} hoverColor={hoverColor} onClick={event => {
                                                event.preventDefault();
                                                // Explicitly select this slide before opening the menu
                                                PresentationEditorController.setCurrentSlide(slideLayout.slide);
                                                this.setState({ contextMenu: { slide: slideLayout.slide, x: event.clientX, y: event.clientY } });
                                            }} />
                                        </SlideControls>
                                        <SlideThumbnail slide={slideLayout.slide}
                                            width={slideLayout.width - 25}
                                            selected={selectedSlides.includes(slideLayout.slide)}
                                            renderCanvas={transitionState == "exited"}
                                            isSkippedSlide={presentation.isSlideSkipped(slideLayout.slide, this.slidesMetadata)}
                                            isOutlineView={this.isOutlineView}
                                            theme={currentTheme}
                                        />
                                        {(isDragging && selectedSlides.length > 1 && selectedSlides.indexOf(slideLayout.slide) == 0) && (
                                            <MultiDragBadge>{selectedSlides.length}</MultiDragBadge>
                                        )}
                                        {!isDragging && !this.isGrid && selectedSlide == slideLayout.slide && selectedSlides.length == 1 && (
                                            <AddSlideButton style={{ width: slideLayout.width - 20 }}>
                                                <Button unframed textColor={buttonColor} hoverColor={hoverColor} onClick={() => PresentationEditorController.showAddSlideDialog()}>
                                                    <Icon white>add_circle</Icon>
                                                    Add Slide
                                                </Button>
                                            </AddSlideButton>
                                        )}
                                        {collaborators.filter(c => c.slideId == slideLayout.slide.id).slice(0, MAX_USERS_TO_RENDER).map(({ photoURL, displayName, email }, i) => (
                                            <Collaborator key={email} style={{ right: 8 + i * 22 * slideLayout.width / 400, transform: `scale(${slideLayout.width / 400})` }}>
                                                <ToolTip title={displayName || email}>
                                                    <Avatar url={photoURL} />
                                                </ToolTip>
                                            </Collaborator>
                                        ))}
                                    </SlideThumbnailContainer>
                                ))}
                                {isSelecting && selectionBox && (
                                    <SelectionMarquee
                                        left={selectionBox.left}
                                        top={selectionBox.top}
                                        width={selectionBox.width}
                                        height={selectionBox.height}
                                    />
                                )}
                                <AbsoluteBox id="scroll-spacer" top={totalHeight} width={100} height={1} />
                            </SlideGrid>

                            {
                                <ControlBar className={`${uiTheme} ${slideNavWidth < 245 ? "small" : ""}`}>
                                    {(!this.isNav) && (
                                        <IconButton icon="chevron_left" color={buttonColor} hoverColor={hoverColor} label="RETURN TO EDITOR" onClick={this.handleCloseGrid} />
                                    )}
                                    {this.isNav && (
                                        <IconButton icon="chevron_left" color={buttonColor} hoverColor={hoverColor} onClick={() => PresentationEditorController.hideSlideNav()} />
                                    )}
                                    {!this.isGrid && (
                                        <>
                                            <FlexSpacer />
                                            <IconButton icon="grid_view" label="GRID VIEW" color={buttonColor} hoverColor={hoverColor} onClick={() => PresentationEditorController.setSlideNavMode("grid")} />
                                        </>
                                    )}
                                    {!this.isNav && (
                                        <FlexBox grow>
                                            <label>{slides.length} Slides</label>
                                        </FlexBox>
                                    )}

                                    {!this.isNav && (
                                        <ThumbnailScaleSlider>
                                            <IconButton icon="remove" color={buttonColor} hoverColor={hoverColor} onClick={() => this.handleSetScale(scale - 1)} />
                                            <Slider width={100}
                                                value={scale}
                                                onChange={this.handleSetScale}
                                                min={0} max={5} step={1}
                                            />
                                            <IconButton icon="add" color={buttonColor} hoverColor={hoverColor} onClick={() => this.handleSetScale(scale + 1)} />
                                        </ThumbnailScaleSlider>
                                    )}
                                </ControlBar>
                            }

                            <Popover open={Boolean(contextMenu)}
                                anchorReference="anchorPosition"
                                anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
                                anchorOrigin={{
                                    vertical: "top",
                                    horizontal: "left",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "left",
                                }}
                                onClose={() => this.setState({ contextMenu: false })}
                            >
                                <ClickAwayListener onClickAway={() => {
                                    this.setState({ contextMenu: false });
                                }}>
                                    <SlideActionsPopupMenu
                                        slides={slides}
                                        presentation={presentation}
                                        selectedSlides={selectedSlides}
                                        closePopup={() => this.setState({ contextMenu: false })}
                                    />
                                </ClickAwayListener>
                            </Popover>
                        </SlideGridContainer>
                    </Container>
                )}
            </Transition>
        );
    }
}

export default PresentationEditorController.withState(SlideNavigatorPanel);
